import { useTheme } from '@mui/material/styles';
import { ColDef } from 'ag-grid-community';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '../common/Button';
import Checkbox from '../common/Checkbox';
import Chips from '../common/Chips';
import ClientSideAgGrid from '../common/customAgGrid/ClientSideAgGrid';
import CustomAgMultiSelectFilter from '../common/customAgGrid/CustomAgMultiSelectFilter';
import CustomAgTextFilter from '../common/customAgGrid/CustomAgTextFilter';
import Modal from '../common/Modal';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

// Custom cell renderer for checkbox selection
const CheckboxCellRenderer = ({ data, selectedProducts, onProductSelect }: any) => {
  return (
    <Checkbox
      checked={selectedProducts.includes(data.id)}
      onChange={() => onProductSelect(data.id)}
    />
  );
};

// Custom cell renderer for status badge using Chips component
const StatusCellRenderer = ({ data }: any) => {
  return <Chips label={data.status as any} />;
};

// Types defined locally for this component
interface LubricationChartProduct {
  id: number;
  productName: string;
  maker: string;
  category: string;
  type: string;
  unit: string;
  status: string;
}

interface ProductAddFlowStep2Props {
  onNext: (_step: number) => void;
  onBack: () => void;
  onClose: () => void;
  onAddToLubricationChart: () => void;
  selectedProducts: number[];
  onProductSelect: (_productId: number) => void;
  hasSelectedProducts: boolean;
  filteredProducts: LubricationChartProduct[];
}

const ProductAddFlowStep2: React.FC<ProductAddFlowStep2Props> = ({
  onNext,
  onBack,
  onClose,
  onAddToLubricationChart,
  selectedProducts,
  onProductSelect,
  hasSelectedProducts,
  filteredProducts,
}) => {
  // Sort state management
  const [tableHeight, setTableHeight] = useState<number>(400);
  const theme = useTheme();
  const { t } = useTranslation();

  // Refs for measuring heights
  const headerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Use refs to store the latest values without causing re-renders
  const selectedProductsRef = useRef(selectedProducts);
  const onProductSelectRef = useRef(onProductSelect);

  // Update refs when props change
  useEffect(() => {
    selectedProductsRef.current = selectedProducts;
    onProductSelectRef.current = onProductSelect;
  }, [selectedProducts, onProductSelect]);

  // Memoize the checkbox cell renderer to prevent re-renders
  // This renderer uses refs so it doesn't need to be recreated when selectedProducts changes
  const checkboxCellRenderer = React.useCallback(
    (params: any) => (
      <CheckboxCellRenderer
        data={params.data}
        selectedProducts={selectedProductsRef.current}
        onProductSelect={onProductSelectRef.current}
      />
    ),
    [],
  ); // Empty dependency array - this function never changes

  // Calculate table height based on available space
  useEffect(() => {
    const calculateHeight = () => {
      if (headerRef.current && containerRef.current) {
        const containerHeight = containerRef.current.clientHeight;
        const headerHeight = headerRef.current.clientHeight;
        // Subtract header height and add some padding buffer (24px for spacing)
        const availableHeight = containerHeight - headerHeight - 24;
        setTableHeight(Math.max(availableHeight, 300)); // Minimum 300px
      }
    };

    // Calculate on mount and when window resizes
    calculateHeight();
    window.addEventListener('resize', calculateHeight);

    // Use ResizeObserver for more accurate tracking
    const resizeObserver = new ResizeObserver(calculateHeight);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      window.removeEventListener('resize', calculateHeight);
      resizeObserver.disconnect();
    };
  }, []);

  // Column definitions for AgGrid - same as step 1
  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        field: 'select',
        headerName: t('table.columns.select'),
        flex: 0,
        width: 90,
        maxWidth: 90,
        minWidth: 90,
        sortable: false,
        filter: false,
        suppressCellFocus: true,
        cellRenderer: checkboxCellRenderer,
      },
      {
        field: 'id',
        headerName: t('table.columns.id'),
        width: 100,
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgTextFilter,
      },
      {
        field: 'productName',
        headerName: t('table.columns.productName'),
        width: 160,
        sortable: true,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'maker',
        headerName: t('table.columns.maker'),
        width: 150,
        sortable: true,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'category',
        headerName: t('table.columns.category'),
        width: 150,
        sortable: true,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'type',
        headerName: t('table.columns.type'),
        width: 150,
        sortable: true,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'unit',
        headerName: t('table.columns.unit'),
        width: 100,
        sortable: true,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'status',
        headerName: t('table.columns.status'),
        width: 150,
        sortable: true,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        cellRenderer: StatusCellRenderer,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
    ],
    [checkboxCellRenderer, t],
  );

  return (
    <Modal
      size="lg"
      open={true}
      onClose={onClose}
      title={t('productAddFlow.step2Title')}
      footer={
        <>
          <Button variant="red" onClick={onBack}>
            {t('buttons.back')}
          </Button>
          {hasSelectedProducts ? (
            <Button variant="outlinedBlue" onClick={onAddToLubricationChart}>
              {t('productAddFlow.addToChartAndContinue')}
            </Button>
          ) : (
            <Button variant="outlinedBlue" onClick={() => onNext(3)}>
              {t('productAddFlow.typeInNotListed')}
            </Button>
          )}
        </>
      }
    >
      <Box
        ref={containerRef}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box ref={headerRef} sx={{ mt: 4, mb: 3, ml: 1 }}>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '16px',
              color: theme.palette.productAddFlow.heading,
              mb: 1,
            }}
          >
            {t('productAddFlow.masterCatalogue')}
          </Typography>
          <Typography
            sx={{
              fontWeight: 400,
              color: theme.palette.productAddFlow.subheading,
              fontSize: '15px',
            }}
          >
            {t('productAddFlow.masterCatalogueDescPart1')}
            <br />
            {t('productAddFlow.masterCatalogueDescPart2')}
          </Typography>
        </Box>
        <Box sx={{ height: `${tableHeight}px`, flex: 1 }}>
          <ClientSideAgGrid
            columnDefs={columnDefs as any}
            rowData={filteredProducts}
            loading={false}
            className="product-add-flow-step2-grid"
            getRowId={(params) => params.data.id}
            onCellClicked={(params: any) => {
              // Only toggle selection when clicking on non-checkbox cells
              // to avoid double-toggle (checkbox onChange + row click)
              if (params.data && params.data.id && params.column.colId !== 'select') {
                onProductSelect(params.data.id);
              }
            }}
            isInModal={true}
          />
        </Box>
      </Box>
    </Modal>
  );
};

export default ProductAddFlowStep2;
